import {CommentBox, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {ReadonlyCommentBox} from '@github-ui/comment-box/ReadonlyCommentBox'
import type {Subject} from '@github-ui/comment-box/subject'
import {ContributorFooter} from '@github-ui/contributor-footer/ContributorFooter'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {type CloseButtonState, IssueActions} from '@github-ui/issue-actions/IssueActions'
import {commitCloseIssueMutation, commitReopenIssueMutation} from '@github-ui/issue-actions/UpdateIssueStateMutation'
import {canBeOptimisticallyParsed} from '@github-ui/md2html'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {testIdProps} from '@github-ui/test-id-props'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {LockIcon} from '@primer/octicons-react'
import {Heading, Link} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useMemo, useRef, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'
import {ConnectionHandler} from 'relay-runtime'

import {BUTTON_LABELS} from '../../constants/button-labels'
import {IDS} from '../../constants/dom-elements'
import {ERRORS} from '../../constants/errors'
import {REACTION_CONTENT, SUBSCRIPTION_FORM_ACTION} from '../../constants/graphql-enums'
import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {useLowQualityWarning} from '../../hooks/use-low-quality-warning'
import {type MarkdownComposerRef, useMarkdownBody} from '../../hooks/use-markdown-body'
import {useMarkdownStyles} from '../../hooks/use-markdown-styles'
import {useMetaAndShiftKeys} from '../../hooks/use-meta-and-shift-keys'
import {addCommentMutation} from '../../mutations/add-comment-mutation'
import {blockedCommentingReason} from '../../utils/blocked-commenting-reason'
import sharedStyles from '../Shared.module.css'
import type {IssueCommentComposer$key} from './__generated__/IssueCommentComposer.graphql'
import type {IssueCommentComposerSecondary$key} from './__generated__/IssueCommentComposerSecondary.graphql'
import type {IssueCommentComposerViewer$key} from './__generated__/IssueCommentComposerViewer.graphql'
import styles from './IssueCommentComposer.module.css'

type IssueCommentComposerBaseProps = {
  onChange: () => void
  onSave: () => void
  onCancel: () => void
  onNewIssueComment?: () => void
  onStateChange?: () => void
  fileUploadsEnabled: boolean
  insideSidePanel: boolean
}

type IssueCommentComposerProps = IssueCommentComposerBaseProps & {
  issue: IssueCommentComposer$key
  issueSecondary?: IssueCommentComposerSecondary$key
  viewer: IssueCommentComposerViewer$key
  ref?: React.Ref<MarkdownComposerRef>
}

export const IssueCommentComposer = ({
  ref,
  onChange,
  onSave,
  onCancel,
  onNewIssueComment,
  onStateChange,
  issue,
  issueSecondary,
  viewer,
  fileUploadsEnabled,
  insideSidePanel,
}: IssueCommentComposerProps) => {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueCommentComposer on Issue {
        id
        locked
        viewerCanComment
        databaseId
        repository {
          id
          databaseId
          isArchived
          nameWithOwner
          viewerCanInteract
          viewerInteractionLimitReasonHTML
        }
        ...IssueActions
      }
    `,
    issue,
  )

  const secondaryData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueCommentComposerSecondary on Issue {
        viewerCanReopen
        viewerCanClose
        authorAssociation
        viewerThreadSubscriptionFormAction
        reactionGroups {
          content
          viewerHasReacted
        }
        discussion {
          url
        }
        repository {
          slashCommandsEnabled
          codeOfConductFileUrl
          securityPolicyUrl
          contributingFileUrl
          supportFileUrl
        }
      }
    `,
    issueSecondary,
  )
  // If the secondary data is not available, we default to false
  const slashCommandsEnabled = secondaryData?.repository.slashCommandsEnabled ?? false

  const viewerData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueCommentComposerViewer on User {
        id
        login
        name
        avatarUrl(size: 64)
      }
    `,
    viewer,
  )

  const {id: issueId} = data
  const commentBoxRef = useRef<CommentBoxHandle>(null)
  const commentComposerRef = useRef<HTMLDivElement>(null)
  const repositoryDatabaseId = data.repository.databaseId!
  const isRepoArchived = data.repository.isArchived
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [closeButtonState, setCloseButtonState] = useState<CloseButtonState>('OPEN')
  const {condensed} = useMarkdownStyles(commentComposerRef)
  const cantCommentReason = blockedCommentingReason(
    isRepoArchived,
    data.locked,
    !data.repository.viewerCanInteract ? data.repository.viewerInteractionLimitReasonHTML : null,
  )
  const {metaAndShiftKeysPressed} = useMetaAndShiftKeys()
  const {markdownBody, resetMarkdownBody, markdownValidationResult, handleMarkdownBodyChanged} = useMarkdownBody({
    commentBoxRef,
    markdownComposerRef: ref ?? null,
    onChange,
    onCancel,
    referenceId: issueId,
    insidePortal: insideSidePanel,
  })

  // Compute viewer's existing state for low-quality warning actions
  // These fields load from secondary query, so default to false until loaded
  const thumbsUpGroup = secondaryData?.reactionGroups?.find(group => group.content === REACTION_CONTENT.THUMBS_UP)
  const viewerHasLiked = thumbsUpGroup?.viewerHasReacted ?? false
  const viewerIsSubscribed = secondaryData?.viewerThreadSubscriptionFormAction === SUBSCRIPTION_FORM_ACTION.UNSUBSCRIBE

  const {
    banner: lowQualityWarningBanner,
    bannerPaddingClassName,
    trackSubmittedWithWarning,
  } = useLowQualityWarning({
    markdownBody,
    issueId: data.id,
    viewerHasLiked,
    viewerIsSubscribed,
    isViewerDataLoaded: !!secondaryData,
    onDismiss: useCallback(() => commentBoxRef.current?.focus(), []),
  })

  const onCommentSave = useCallback(
    (subject: string, skipStateChange = false) => {
      const withOptimisticResponse = canBeOptimisticallyParsed(markdownBody)
      setIsSubmitting(true)
      if (markdownBody?.length > 0) {
        // Track analytics if low-quality warning was shown
        trackSubmittedWithWarning(markdownBody)
        // Store the original markdown body value to restore if mutation fails
        const originalMarkdownBody = markdownBody

        // Reset the markdown body before starting the mutation
        resetMarkdownBody()

        const connectionFilters = {
          visibleEventsOnly: true,
        }
        const newTimelineConnectionId = ConnectionHandler.getConnectionID(
          data.id,
          'Issue__backTimelineItems',
          connectionFilters,
        )
        const issueCommentsConnectionId = ConnectionHandler.getConnectionID(
          data.id, // passed as input to the mutation/subscription
          'IssueBacksideTimeline_timelineItems',
          connectionFilters,
        )
        const connectionId = newTimelineConnectionId ?? issueCommentsConnectionId

        addCommentMutation({
          environment,
          input: {
            subject,
            body: originalMarkdownBody,
            connectionId,
          },
          onError: () => {
            setIsSubmitting(false)

            // Restore the original markdown body value if mutation fails
            handleMarkdownBodyChanged(originalMarkdownBody)

            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: ERRORS.couldNotComment,
            })
          },
          onCompleted: () => {
            setIsSubmitting(false)
            onNewIssueComment?.()
          },
          withOptimisticResponse,
          optimisticInput: secondaryData
            ? {
                commentAuthor: {
                  id: viewerData.id,
                  login: viewerData.login,
                  avatarUrl: viewerData.avatarUrl,
                  name: viewerData.name,
                },
                repositoryId: data.repository.id,
                authorAssociation: secondaryData.authorAssociation,
              }
            : undefined,
        })
      }

      // Do not modify the issue state when we are already modifying the issue state elsewhere
      if (metaAndShiftKeysPressed && !skipStateChange) {
        if (closeButtonState === 'CLOSED' && secondaryData?.viewerCanClose) {
          commitCloseIssueMutation({
            environment,
            input: {
              issueId: data?.id,
              newStateReason: 'COMPLETED',
            },
            onError: () =>
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addToast({
                type: 'error',
                message: ERRORS.couldNotCloseIssue,
              }),
            onCompleted: () => {
              onNewIssueComment?.()
              onStateChange?.()
            },
          })
        } else if (closeButtonState === 'NOT_PLANNED' && secondaryData?.viewerCanClose) {
          commitCloseIssueMutation({
            environment,
            input: {
              issueId: data?.id,
              newStateReason: 'NOT_PLANNED',
            },
            onError: () =>
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addToast({
                type: 'error',
                message: ERRORS.couldNotCloseIssue,
              }),
            onCompleted: () => {
              onNewIssueComment?.()
              onStateChange?.()
            },
          })
        } else if (closeButtonState === 'OPEN' && secondaryData?.viewerCanReopen) {
          commitReopenIssueMutation({
            environment,
            input: {
              issueId: data?.id,
            },
            onError: () =>
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addToast({
                type: 'error',
                message: ERRORS.couldNotCloseIssue,
              }),
            onCompleted: () => {
              onNewIssueComment?.()
              onStateChange?.()
            },
          })
        }
      }

      onSave()
    },
    [
      markdownBody,
      metaAndShiftKeysPressed,
      onSave,
      data.id,
      data.repository.id,
      environment,
      viewerData.id,
      viewerData.login,
      viewerData.avatarUrl,
      viewerData.name,
      secondaryData,
      addToast,
      resetMarkdownBody,
      handleMarkdownBodyChanged,
      onNewIssueComment,
      onStateChange,
      closeButtonState,
      trackSubmittedWithWarning,
    ],
  )

  const subject = useMemo<Subject>(() => {
    return {
      type: 'issue_comment',
      id: {
        id: data.id,
        databaseId: data.databaseId!,
      },
      repository: {
        databaseId: repositoryDatabaseId,
        nwo: data.repository.nameWithOwner,
        slashCommandsEnabled,
      },
    }
  }, [data.databaseId, data.id, data.repository.nameWithOwner, repositoryDatabaseId, slashCommandsEnabled])

  const handleActionButtonUpdate = useCallback(() => {
    if (markdownValidationResult && markdownValidationResult.isValid) {
      onCommentSave(issueId, true)
    }
  }, [issueId, onCommentSave, markdownValidationResult])

  const actions = (
    <IssueActions
      actionRef={data}
      onAction={handleActionButtonUpdate}
      hasComment={markdownBody.length > 0}
      buttonSize="medium"
      closeButtonState={closeButtonState}
      setCloseButtonState={setCloseButtonState}
      viewerCanReopen={secondaryData?.viewerCanReopen}
      viewerCanClose={secondaryData?.viewerCanClose}
      onStateChange={onStateChange}
    />
  )

  const issuesReactLazyLoad = isFeatureEnabled('issues_lazy_load_comment_box_suggestions')

  const contributingFileUrl = secondaryData?.repository.contributingFileUrl || undefined
  const codeOfConductFileUrl = secondaryData?.repository.codeOfConductFileUrl || undefined
  const securityPolicyUrl = secondaryData?.repository.securityPolicyUrl || undefined
  const supportFileUrl = secondaryData?.repository.supportFileUrl || undefined
  const renderContributorFooter = !!secondaryData?.repository
  const {isBrowser} = useRuntimeEnvironment()

  return (
    <div
      data-testid={TEST_IDS.commentComposer}
      className={styles.commentComposerWrapper}
      ref={commentComposerRef}
      data-inside-side-panel={insideSidePanel}
      id={IDS.issueCommentComposer}
    >
      <div className={styles.commentComposer}>
        <Link
          href={`/${viewerData.login}`}
          {...getHovercardAttributesForType('user', {login: viewerData.login})}
          aria-label={`@${viewerData.login}'s profile`}
          className={styles.avatarLink}
        >
          <GitHubAvatar size={40} src={viewerData.avatarUrl} alt={viewerData.login} />
        </Link>
        <Heading
          // `data-jump-to-bottom-target` is used to declare this as a target for the 'jump to bottom' button, which
          // will move focus to this heading when clicked. To preserve focus and ensure the target is announced for
          // screen readers, we also add `tabIndex={-1}` to the heading. This ensures it is not normally focusable
          // except for programmatically or as the target of a link
          data-jump-to-bottom-target
          tabIndex={-1}
          id="comment-composer-heading"
          as="h2"
          className={styles.heading}
        >
          Add a comment
        </Heading>
        <div className={clsx(styles.commentBoxWrapper, bannerPaddingClassName)}>
          {isBrowser &&
            (!data.viewerCanComment || !data.repository.viewerCanInteract || secondaryData?.discussion ? (
              <ReadonlyCommentBox
                reason={
                  secondaryData?.discussion ? (
                    <CommentDisabledDueToDiscussion discussionUrl={secondaryData?.discussion?.url ?? ''} />
                  ) : (
                    cantCommentReason
                  )
                }
                icon={LockIcon}
                {...testIdProps(TEST_IDS.readonlyCommentBox(TEST_IDS.commentComposer))}
              />
            ) : (
              <CommentBox
                ref={commentBoxRef}
                placeholder={LABELS.newCommentPlaceholder}
                label={LABELS.newComment}
                disabled={isSubmitting}
                subject={subject}
                infoBanner={lowQualityWarningBanner}
                /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- TODO: [SECURITY] Potential XSS risk. Pre-existing infraction - DO NOT COPY. Must be fixed. */
                value={markdownBody as SafeHTMLString}
                onChange={handleMarkdownBodyChanged}
                actions={actions}
                validationResult={markdownValidationResult}
                saveButtonText={BUTTON_LABELS.comment}
                saveButtonTrailingIcon={false}
                onSave={() => onCommentSave(issueId)}
                fileUploadsEnabled={fileUploadsEnabled}
                buttonSize="medium"
                labelledBy="comment-composer-heading"
                teamHovercardsEnabled
                markdownSuggestionsFetchMethod={issuesReactLazyLoad ? 'lazy' : 'eager'}
                className={clsx(sharedStyles.CommentBox, condensed && sharedStyles.Condensed)}
                {...testIdProps(TEST_IDS.commentBox(TEST_IDS.commentComposer))}
              />
            ))}
          {renderContributorFooter && (
            <ContributorFooter
              contributingFileUrl={contributingFileUrl}
              codeOfConductFileUrl={codeOfConductFileUrl}
              securityPolicyUrl={securityPolicyUrl}
              supportFileUrl={supportFileUrl}
              className={styles.ContributorFooter}
            />
          )}
        </div>
      </div>
    </div>
  )
}
IssueCommentComposer.displayName = 'CommentComposer'

const CommentDisabledDueToDiscussion = ({discussionUrl}: {discussionUrl: string}) => (
  <div>
    {LABELS.issueConvertedToDiscussion}&nbsp;
    <Link href={discussionUrl} inline>
      discussion
    </Link>
  </div>
)
